import {inject, Injectable} from '@angular/core';
import {DocumentNode, GraphQLError} from "graphql";
import {TypedDocumentNode} from "@graphql-typed-document-node/core";
import {ApolloQueryResult, OperationVariables} from "@apollo/client/core/types";
import {BehaviorSubject, catchError, EMPTY, lastValueFrom, map, Observable, of, tap} from "rxjs";
import {Apollo, gql} from "apollo-angular";
import {HelperTools} from "../utils/tools/helper-tools";
import {filter, finalize, first} from "rxjs/operators";

/**
 * @author Carlos Duardo <carlos.duardo@qualud.com>
 */
export const INVALID_MUTATION_ID = 'invalid_clientMutationId';
export const LOCAL_ERROR = 'local_error';

export type ApolloMutationError = {
  message: string,
  debugMessage: string,
  extensions: {
    category: string,
    debugMessage?: string,
    status?: number,
    violations?: [{ path: string, message: string }]
  }
}

/**
 * @author Carlos Duardo <carlos.duardo@qualud.com>
 */
export declare type ApolloFormError = {
  errors: ApolloMutationError[]
}

/**
 * @author Carlos Duardo <carlos.duardo@qualud.com>
 */
export declare type ApolloQuery = DocumentNode | TypedDocumentNode<any, OperationVariables>
/**
 * @author Carlos Duardo <carlos.duardo@qualud.com>
 */
export declare type ApolloMutation = DocumentNode | TypedDocumentNode<any, OperationVariables>
/**
 * @author Carlos Duardo <carlos.duardo@qualud.com>
 */
export declare type ApolloMutationResult<T = any> = { success: true, data: T } | {
  success: false, errors: ApolloMutationError[]
}
/**
 * @author Carlos Duardo <carlos.duardo@qualud.com>
 */
export declare type QueryResult<T = any> = { data: T | undefined, errors: GraphQLError[] | undefined }

/**
 * @author Carlos Duardo <carlos.duardo@qualud.com>
 */
export enum MUTATION_ACTION {
  create = 'create',
  update = 'update',
  delete = 'delete'
}

/**
 * @author Carlos Duardo <carlos.duardo@qualud.com>
 */
export namespace MUTATION_ACTION {
  /**
   * @author Carlos Duardo <carlos.duardo@qualud.com>
   */
  export function getAllValues() {
    return Object.values(MUTATION_ACTION);
  }

  /**
   * @author Carlos Duardo <carlos.duardo@qualud.com>
   */
  export function isAllowedValue(action: string): boolean {
    // @ts-ignore
    return getAllValues().includes(action);
  }
}

/**
 * @author Carlos Duardo <carlos.duardo@qualud.com>
 */
export type MutationAction = MUTATION_ACTION | string

export const ENTITY_FORM_VIOLATION_CODE = 422;

@Injectable({
  providedIn: 'root'
})
/**
 * @author Carlos Duardo <carlos.duardo@qualud.com>
 */
export class ApolloHelperService {

  protected apollo: Apollo = inject(Apollo);

  /**
   * Fetch one resource by id
   * @param options
   * @return Observable
   *
   * @author Carlos Duardo <carlos.duardo@qualud.com>
   */
  public fetchById<T = any>(options: {
    query: ApolloQuery,
    id: string,
    isLoading$?: BehaviorSubject<boolean>
    freezeResult?: boolean,
    convertTo?: undefined
  }): Observable<QueryResult<T>>
  /**
   * Fetch one resource by id
   * @param options
   * @return Observable
   *
   * @author Carlos Duardo <carlos.duardo@qualud.com>
   */
  public fetchById<T = any>(options: {
    query: ApolloQuery,
    id: string,
    isLoading$?: BehaviorSubject<boolean>,
    freezeResult?: boolean,
    convertTo?: 'observable'
  }): Observable<QueryResult<T>>
  /**
   * Fetch one resource by id
   * @param options
   * @return Promise
   *
   * @author Carlos Duardo <carlos.duardo@qualud.com>
   */
  public fetchById<T = any>(options: {
    query: ApolloQuery,
    id: string,
    isLoading$?: BehaviorSubject<boolean>,
    freezeResult?: boolean,
    convertTo?: 'promise'
  }): Promise<QueryResult<T>>
  /**
   * @author Carlos Duardo <carlos.duardo@qualud.com>
   */
  public fetchById<T = any>(
    options: {
      /**
       * Graphql query
       */
      query: ApolloQuery,
      /**
       * ID for searching an exact object
       */
      id: string,
      /**
       * Alternative you can pass a boolean observable to the query, and automatically receive the information if query is loading or not
       * it's quite handy when you want to show loading state for a query
       */
      isLoading$?: BehaviorSubject<boolean>,
      freezeResult?: boolean,
      convertTo?: 'observable' | 'promise',
    }): Observable<QueryResult<T>> | Promise<QueryResult<T>> {
    const {
      id,
      convertTo,
    } = options

    if ('promise' === convertTo) {
      return this.fetch({
        ...options,
        convertTo: 'promise',
        isCollection: false,
        variables: {id}
      })
    }

    return this.fetch({
      ...options,
      convertTo: 'observable',
      isCollection: false,
      variables: {id}
    })
  }


  public fetch<T = any>(options: {
    query: ApolloQuery,
    isCollection?: boolean,
    freezeResult?: boolean,
    variables?: any,
    isLoading$?: BehaviorSubject<boolean>,
    convertTo?: 'observable',
  }): Observable<QueryResult<T>>

  public fetch<T = any>(
    options: {
      query: ApolloQuery,
      isCollection?: boolean,
      freezeResult?: boolean,
      variables?: any,
      isLoading$?: BehaviorSubject<boolean>,
      convertTo?: 'promise',
    }
  ): Promise<QueryResult<T>>


  /**
   * Make a query to the server and return a promise or observable with the value or errors if exists
   * @param options
   * @return Promise
   *
   * @author Carlos Duardo <carlos.duardo@qualud.com>
   */
  public fetch<T = any>(
    options: {
      query: ApolloQuery,
      isCollection?: boolean,
      freezeResult?: boolean,
      variables?: any,
      isLoading$?: BehaviorSubject<boolean>,
      convertTo?: 'observable' | 'promise',
    }
  ): Observable<QueryResult<T>> | Promise<QueryResult<T>> {

    let {
      query,
      variables,
      isLoading$,
      isCollection = true,
      freezeResult = true,
      convertTo,
    } = options;


    isLoading$?.next(true);

    const source$ = this.apollo.query<T>({
      errorPolicy: "all",
      query,
      variables
    })
      .pipe(
        first(),
        map((result) => {
          let response = apolloGetQueryData<T>({result, isCollection});

          let queryData: QueryResult<T> = {
            data: undefined,
            errors: undefined,
          }

          if (true === response?.success) {
            queryData.data = freezeResult ? response.data : structuredClone<T>(response.data);
          }

          if (false === response?.success) {
            queryData.errors = response.errors;
          }

          return queryData;
        }),
        catchError(err => of({data: undefined, errors: err})),
        finalize(() => isLoading$?.next(false))
      );


    return 'promise' === convertTo
      ? lastValueFrom<QueryResult<T>>(source$)
      : source$;
  }

  /**
   * Perform mutations and return a promise when it returns successfully from the server
   * and rejects it when there are errors, as well as checking the integrity of clientMutationId
   * if specified
   *
   * @param mutation
   * @param variables
   * @param enabledSecurity
   * @param isLoadingObserver
   *
   * @author Carlos Duardo <carlos.duardo@qualud.com>
   */
  public async mutate(
    {
      mutation,
      variables,
      isLoading$,
      enabledSecurity = true,
      processResult = true,
    }: {
      mutation: ApolloMutation,
      variables: any,
      isLoading$?: BehaviorSubject<boolean>,
      enabledSecurity?: boolean
      processResult?: boolean
    },
  ): Promise<ApolloMutationResult> {

    isLoading$?.next(true);

    if (enabledSecurity) {
      apolloGuaranteeMutationIdInVars(variables)
    }

    const source$ = this.apollo.mutate({
        errorPolicy: 'all',
        mutation,
        variables
      })
        .pipe(
          map(result => {
            let {data, loading, errors} = result;

            let response: any = {};

            isLoading$?.next(loading);

            if (undefined !== errors && errors.length > 0) {
              response = {success: false, errors};
            }

            // //if there is an answer
            if (undefined === errors && undefined !== data) {
              //if security is activated we check that the initial clientMutationId is the same as the server responded
              if (enabledSecurity && apolloGetMutationIdFromVariables(variables) !== apolloGetMutationIdFromResponse(data)) {
                response = {
                  success: false, errors: [{extensions: {category: INVALID_MUTATION_ID}}]
                };
              }

              const apolloMutationData = apolloGetMutationData(data);
              response = {
                success: true,
                data: processResult && apolloMutationData?.success ? apolloMutationData.data : data
              }
            }

            return response
          }),
          catchError(err => of({
            success: false,
            errors: [{
              extensions: {category: LOCAL_ERROR},
              debugMessage: err.message
            }]
          })),
          finalize(() => isLoading$?.next(false))
        )
    ;

    return lastValueFrom(source$);
  }

}


//begin helper methods with tree-shake support

/**
 * Get the clientMutationId of the variables sent to the mutation
 * @param variables
 *
 * @author Carlos Duardo <carlos.duardo@qualud.com>
 */
export function apolloGetMutationIdFromVariables(variables: any): string | undefined {
  if (variables.hasOwnProperty('input')) {
    return variables.input.clientMutationId;
  }

  return variables.clientMutationId;
}

/***
 * Get the clientMutationId from the server response
 * @param data
 *
 * @author Carlos Duardo <carlos.duardo@qualud.com>
 */
export function apolloGetMutationIdFromResponse(data: any): string | undefined {
  let keyMutation = apolloGetConnectionName(data);
  if (keyMutation) {
    return data[keyMutation]?.clientMutationId
  }
  return undefined
}

/**
 * Processes an ApolloQueryResult and returns an object with key success = true and key data with the value of the query
 * as long as there are no errors, in case of error it returns success = false and the errors key with the query errors
 * @param options
 * @return { success: true, data: T } | { success: false, errors: any } | undefined
 */
export function

apolloGetQueryData<T = any>(
  options: {
    result: ApolloQueryResult<any>,
    isCollection: boolean,
  }
): { success: true, data: T } | { success: false, errors: any } | undefined {

  let {
    data,
    errors,
  } = options.result;


  if (undefined !== errors) {
    return {
      success: false,
      errors
    }
  }

  if (undefined !== data) {
    let apolloConnectionName = apolloGetConnectionName(data);

    if (apolloConnectionName) {
      return {
        success: true,
        data: options.isCollection ? data[apolloConnectionName].collection : data[apolloConnectionName]
      }
    }
  }

  return undefined;
}


/**
 * Processes the response returned by the graphql mutation and infers the value of the structure to be able to return the specific data,
 * If nothing is found, it returns undefined
 * @example
 * let mutationResultData1 = {
 *    "updateGroup": {
 *      "clientMutationId": "ç#Zzkh!YmV2c",
 *      "group": {
 *        "id": "/api/groups/1",
 *        "name": "New Group"
 *      },
 *      "__typename": "updateGroupPayload"
 *    }
 * }
 *
 * let mutationResultData2 = {
 *    "updateGroup": {
 *      "clientMutationId": "ç#Zzkh!YmV2c",
 *      "__typename": "updateGroupPayload"
 *    }
 * }
 *
 * let data1 = apolloGetMutationData(mutationResultData1);
 * let data2 = apolloGetMutationData(mutationResultData2);
 * console.log(data1)//{ "id": "/api/groups/1", "name": "New Group"}
 * console.log(data2)// undefined
 *
 * @param mutationResultData
 * @return {success: boolean, data: any} | undefined
 */
export function apolloGetMutationData(mutationResultData: any): { success: boolean, data: any } | undefined {
  if (undefined === mutationResultData) {
    return undefined;
  }

  let apolloConnectionName = apolloGetConnectionName(mutationResultData);

  if (undefined !== apolloConnectionName) {

    let definitionName = apolloConnectionName;

    const index = definitionName.search(/[A-Z]/); // find the index of the first uppercase letter
    const split = definitionName.slice(index); // get the second part of the string
    const firstLetterLowercase = split.charAt(0).toLowerCase(); // get the first letter of the second part and convert it to lowercase
    definitionName = firstLetterLowercase + split.slice(1)

    const result = mutationResultData[apolloConnectionName][definitionName] || undefined;

    return {
      success: undefined !== result,
      data: result,
    }
  }

}

/**
 * It allows obtaining the initial key of the structure of a graphql response, in the case of queries it would be the name of the requested resource,
 * and for mutations it would be the value of the name of the operation carried out
 * @param data
 */
export function apolloGetConnectionName(data: any): string | undefined {
  return Object.keys(data)[0];
}

/**
 * Returns the error message from a ApolloFormError response, if it is of type "internal" o "graphql"
 *
 * @param {ApolloFormError} reason
 * @return string | undefined
 *
 * @author Carlos Duardo <carlos.duardo@qualud.com>
 */
export function apolloGetProcessedErrorFromResponse(reason: ApolloFormError): {
  category: string,
  message?: string
} | undefined {
  const firstReasonElement = reason.errors[0];

  const isInvalidMutation = firstReasonElement.message
    .toLowerCase()
    .replaceAll('"', '')
    .includes('variable $input got invalid');

  if (isInvalidMutation) {
    firstReasonElement.extensions.category = 'graphql'
  }

  const errorCategory = firstReasonElement?.extensions?.category?.toLowerCase();
  const errorStatus = firstReasonElement?.extensions?.status;

  if (ENTITY_FORM_VIOLATION_CODE === errorStatus) return undefined;

  switch (errorCategory) {
    case INVALID_MUTATION_ID.toLowerCase():
      return {
        category: 'INVALID CSRF TOKEN',
        message: 'invalid_client_mutation_id'
      };
    case LOCAL_ERROR:
    case 'internal':
      return {
        category: firstReasonElement.message,
        message: firstReasonElement.debugMessage,
      };
    case 'graphql':
      return {
        category: firstReasonElement.extensions.category + ' server error',
        message: firstReasonElement.message,
      };
    case undefined:
      return {
        category: 'unknown server error',
        message: firstReasonElement.extensions?.debugMessage ?? firstReasonElement.message,
      };
    default:
      return {
        category: firstReasonElement?.extensions?.category + ' server error',
        message: firstReasonElement.message,
      };
  }
}


/**
 * Generates mutations dynamically based on API-PLATFORM standards
 * @param entityName
 * @param action
 * @param queryFields
 * @param suffix
 *
 * @author Carlos Duardo <carlos.duardo@qualud.com>
 */
export function apolloCreateGenericMutation(entityName: string, action: MutationAction | string, queryFields?: string, suffix?: string) {
  let operationName = action.toString() + entityName;

  suffix = undefined !== suffix ? suffix : 'Instance';

  let queryTemplate = '';
  if (null !== queryFields && undefined !== queryFields) {
    queryTemplate = `${entityName.charAt(0).toLowerCase() + entityName.slice(1)} {
        ${queryFields}
    }
    `
  }

  let template = `mutation ${operationName + suffix} ($input: ${operationName}Input!) {
          ${operationName}(input: $input){
          clientMutationId
          ${queryTemplate}
          }
        }`

  return gql`${template}`
}

/***
 * Guarantees that in the mutation variables there is clientMutationId with value
 * otherwise create and assign a default value
 * @param variables
 * @param hashIdLength
 * @private
 *
 * @author Carlos Duardo <carlos.duardo@qualud.com>
 */
function apolloGuaranteeMutationIdInVars(variables: any, hashIdLength: number = 12): void {
  if (undefined === apolloGetMutationIdFromVariables(variables)) {
    const hash = HelperTools.generateHash({
      length: hashIdLength,
      hasSpecialCharacters: true
    });
    if (variables.hasOwnProperty('input')) {
      variables.input.clientMutationId = hash;
    } else {
      variables.clientMutationId = hash;
    }
  }

}


/**
 * It automates the work of processing a QueryResult of type collection (findAll) or simple (findOneBy), this helper function
 * processes the observable and returns the pure value of the query, as well checks the existence of errors and if they exist,
 * returns null, this way our flow is not broken by uncontrolled errors.
 *
 * It also allows us to throw a console error if we wish.
 *
 * It is ideal for flows where it is not required to send alerts to the user and the flow of the app should not be stopped either,
 * in case of errors the app would work as if there were no data retrieved.
 *
 * Note:
 * It is important to understand that the observable should not be infinite or, as it is better known, a Hot Observable liken event clicks or watch queries,
 * like those of the HttpClient or those of the ApolloHelperService, pass an infinite observables can cause memory leaks,
 * and you should be aware of destroying it.
 *
 * @example
 * const queryResult = apolloGetDataFromObservableQueryResult(this.apolloHelper.fetch(...queryOptions))
 *
 * @author Carlos Duardo <carlos.duardo@qualud.com>
 */
export function apolloGetDataFromObservableQueryResult<TValue = any>(
  observable: Observable<QueryResult<TValue>>,
  options?: {
    defaultValue?: TValue,
    onErrorCallback?: (error: any) => void,
  }): Observable<TValue> {
  return observable.pipe(
    tap((value) => {
      if (value.errors) {
        throw new Error(value.errors[0].message);
      }
    }),
    filter((value) => undefined !== value.data),
    map((queryResponse) => queryResponse.data as TValue),
    catchError(error => {

      if (undefined !== options?.onErrorCallback) {
        options.onErrorCallback(error);
      } else {
        console.error('apolloGetDataFromObservableQueryResult', error);
      }

      return options?.defaultValue ? of<TValue>(options.defaultValue) : EMPTY;
    }),
  )
}
